/**
 * Protected Route Components
 * Handles authentication and role-based access control for routes
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type UserRole } from '../context/AuthContext';
import { isSafeRedirect } from '../utils/redirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required roles to access this route (if empty, just requires authentication) */
  allowedRoles?: UserRole[];
  /** Redirect path if not authenticated (default: /login) */
  redirectTo?: string;
  /** Redirect path if authenticated but wrong role (default: /) */
  forbiddenRedirectTo?: string;
  /** Whether to require email verification */
  requireVerified?: boolean;
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-50">
      <div className="text-center">
        <div className="inline-block w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        <p className="mt-4 text-warm-600">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Protected Route - Requires authentication
 * Optionally restricts to specific roles
 */
export function ProtectedRoute({
  children,
  allowedRoles = [],
  redirectTo = '/login',
  forbiddenRedirectTo = '/',
  requireVerified = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    // Save the attempted URL for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check email verification if required
  if (requireVerified && !user.email_verified) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={forbiddenRedirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Public Only Route - Redirects authenticated users away
 * Useful for login/register pages
 */
interface PublicOnlyRouteProps {
  children: React.ReactNode;
  /** Where to redirect authenticated users */
  redirectTo?: string;
}

export function PublicOnlyRoute({
  children,
  redirectTo,
}: PublicOnlyRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated && user) {
    // Redirect to the page they came from — router state (in-app flows) or the
    // ?redirect= param (email deep-links, e.g. the SDA-265 balance-payment
    // request). The register page logs the user in mid-activation, and this
    // guard unmounts it before its own navigate(from) runs — so the guard must
    // honor the same destination or the deep-link silently degrades to the
    // dashboard. isSafeRedirect rejects external URLs and auth self-loops.
    const stateFrom = location.state?.from?.pathname as string | undefined;
    const paramRedirect = new URLSearchParams(location.search).get('redirect');
    const from = isSafeRedirect(stateFrom)
      ? stateFrom
      : isSafeRedirect(paramRedirect)
        ? paramRedirect
        : undefined;
    const defaultRedirect = redirectTo || getDashboardPath(user.role);
    return <Navigate to={from || defaultRedirect} replace />;
  }

  return <>{children}</>;
}

/**
 * Get dashboard path based on user role
 */
function getDashboardPath(role: UserRole): string {
  switch (role) {
    case 'studio':
      return '/dashboard';
    case 'faculty':
      return '/faculty';
    case 'admin':
    case 'super_admin':
      return '/admin';
    default:
      return '/';
  }
}

/**
 * Studio Route - Requires studio role
 */
export function StudioRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['studio']} forbiddenRedirectTo="/">
      {children}
    </ProtectedRoute>
  );
}

/**
 * Faculty Route - Requires faculty role
 */
export function FacultyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['faculty']} forbiddenRedirectTo="/">
      {children}
    </ProtectedRoute>
  );
}

/**
 * Admin Route - Requires admin or super_admin role
 */
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['admin', 'super_admin']} forbiddenRedirectTo="/">
      {children}
    </ProtectedRoute>
  );
}

/**
 * Super Admin Route - Requires super_admin role
 */
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['super_admin']} forbiddenRedirectTo="/">
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;
